import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'

import { AdvisorViewStatus } from '@/types/enums/advisor-view-status'
import { OrderSide } from '@/types/enums/order'
import { TraderViewStatus } from '@/types/enums/trader-view-status'

import {
  ActivePanelType,
  type OrderBookRowItem,
  type OrderSummaryData,
} from './types'

// Constants
const MERGE_STATE = false

// Default values
export const defaultSummaryData: OrderSummaryData = {
  bidCount: 0,
  askCount: 0,
  bidRange: '—',
  askRange: '—',
  marketMid: '—',
  bidEnabledCount: 0,
  askEnabledCount: 0,
}

export const defaultEmptyOrder: OrderBookRowItem = {
  bidPrice: null,
  bidYieldToWorst: null,
  bidNumBonds: 0,
  bidMinNumBonds: 0,
  bidMinBalanceRemaining: null,
  bidMinIncrement: null,
  bidVenue: '—',
  bidSpreadToBenchmarkBps: null,
  askPrice: null,
  askYieldToWorst: null,
  askNumBonds: 0,
  askMinNumBonds: 0,
  askMinBalanceRemaining: null,
  askMinIncrement: null,
  askVenue: '—',
  askSpreadToBenchmarkBps: null,
}

// Zustand store interface
interface OrderBookStore {
  // State
  activePanel: ActivePanelType
  isCollapsed: boolean
  orders: OrderBookRowItem[]
  summaryData: OrderSummaryData
  isEmpty: boolean

  // Actions
  setActivePanel: (panel: ActivePanelType) => void
  selectSide: (side: ActivePanelType) => void
  setIsCollapsed: (collapsed: boolean) => void
  toggleCollapse: () => void
  setOrders: (orders: OrderBookRowItem[]) => void
  setSummaryData: (data: OrderSummaryData) => void
  setIsEmpty: (isEmpty: boolean) => void
  updateFromProps: (
    orders: OrderBookRowItem[],
    summaryData: OrderSummaryData,
    isEmpty: boolean,
  ) => void
  updateCollapseState: (status?: TraderViewStatus | AdvisorViewStatus) => void
  initializeWithOrderSide: (orderSide?: OrderSide.Buy | OrderSide.Sell) => void

  // Computed getters
  getBids: () => OrderBookRowItem[]
  getAsks: () => OrderBookRowItem[]
  isPanelActive: (panel: ActivePanelType) => boolean
}

// Helper functions
const getInitialActivePanelFromOrderSide = (
  orderSide?: OrderSide.Buy | OrderSide.Sell,
): ActivePanelType => {
  if (orderSide === OrderSide.Sell) {
    return ActivePanelType.Bid // Sell orders default to bids
  } else if (orderSide === OrderSide.Buy) {
    return ActivePanelType.Ask // Buy orders default to asks
  }
  return ActivePanelType.Both // Default to both
}

const getCollapseStateFromStatus = (
  status?: TraderViewStatus | AdvisorViewStatus,
): boolean => {
  if (!status) return false

  const collapseStatuses = [
    TraderViewStatus.PendingExecution,
    TraderViewStatus.PendingRFQ,
    TraderViewStatus.RFQResponseRequired,
    AdvisorViewStatus.PendingExecution,
  ]

  const expandStatuses = [
    TraderViewStatus.PendingApproval,
    TraderViewStatus.ApprovalRequired,
    TraderViewStatus.RoutingRequired,
    AdvisorViewStatus.PendingApproval,
  ]

  if (collapseStatuses.includes(status)) {
    return true
  } else if (
    expandStatuses.includes(status) ||
    !collapseStatuses.includes(status)
  ) {
    return false
  }

  return false
}

// Create the Zustand store
export const useOrderBookStore = create<OrderBookStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      activePanel: ActivePanelType.Both,
      isCollapsed: true,
      orders: [defaultEmptyOrder],
      summaryData: defaultSummaryData,
      isEmpty: true,

      // Actions
      setActivePanel: (activePanel) =>
        set({ activePanel }, MERGE_STATE, 'setActivePanel'),

      selectSide: (side) =>
        set(
          (state) => ({
            activePanel:
              state.activePanel === side ? ActivePanelType.Both : side,
          }),
          MERGE_STATE,
          'selectSide',
        ),

      setIsCollapsed: (isCollapsed) =>
        set({ isCollapsed }, MERGE_STATE, 'setIsCollapsed'),

      toggleCollapse: () =>
        set(
          (state) => ({ isCollapsed: !state.isCollapsed }),
          MERGE_STATE,
          'toggleCollapse',
        ),

      setOrders: (orders) =>
        set({ orders, isEmpty: orders.length === 0 }, MERGE_STATE, 'setOrders'),

      setSummaryData: (summaryData) =>
        set({ summaryData }, MERGE_STATE, 'setSummaryData'),

      setIsEmpty: (isEmpty) => set({ isEmpty }, MERGE_STATE, 'setIsEmpty'),

      updateFromProps: (orders, summaryData, isEmpty) =>
        set({ orders, summaryData, isEmpty }, MERGE_STATE, 'updateFromProps'),

      updateCollapseState: (status) =>
        set(
          { isCollapsed: getCollapseStateFromStatus(status) },
          MERGE_STATE,
          'updateCollapseState',
        ),

      initializeWithOrderSide: (orderSide) =>
        set(
          { activePanel: getInitialActivePanelFromOrderSide(orderSide) },
          MERGE_STATE,
          'initializeWithOrderSide',
        ),

      // Computed getters (derived state)
      getBids: () => get().orders.filter((order) => order.bidPrice !== null),

      getAsks: () => get().orders.filter((order) => order.askPrice !== null),

      isPanelActive: (panel: ActivePanelType) => {
        const { activePanel } = get()
        return activePanel === panel || activePanel === ActivePanelType.Both
      },
    })),
    {
      name: 'order-book-store',
    },
  ),
)

// Selectors for better performance (prevent unnecessary re-renders)
export const selectActivePanel = (state: OrderBookStore) => state.activePanel
export const selectIsCollapsed = (state: OrderBookStore) => state.isCollapsed
export const selectOrders = (state: OrderBookStore) => state.orders
export const selectSummaryData = (state: OrderBookStore) => state.summaryData
export const selectIsEmpty = (state: OrderBookStore) => state.isEmpty

// Individual action selectors for stable references
export const selectSetActivePanel = (state: OrderBookStore) =>
  state.setActivePanel
export const selectSelectSide = (state: OrderBookStore) => state.selectSide
export const selectSetIsCollapsed = (state: OrderBookStore) =>
  state.setIsCollapsed
export const selectToggleCollapse = (state: OrderBookStore) =>
  state.toggleCollapse
export const selectUpdateFromProps = (state: OrderBookStore) =>
  state.updateFromProps
export const selectUpdateCollapseState = (state: OrderBookStore) =>
  state.updateCollapseState
export const selectInitializeWithOrderSide = (state: OrderBookStore) =>
  state.initializeWithOrderSide
export const selectIsPanelActive = (state: OrderBookStore) =>
  state.isPanelActive
